import { HttpClient } from "@angular/common/http";
import { inject, Injectable } from "@angular/core";
import { environment } from "../../../../Environments/Environment";
import { WarehouseSearchResponse } from "../Dto/WarehouseSearchResponse";
import { WarehouseSearchRequest } from "../Dto/WarehouseSearchRequest";
import { Warehouse } from "../Dto/WarehouseModel";
import { CreateWarehouseRequest } from "../Dto/CreateWarehouseRequest";
import { firstValueFrom } from "rxjs";

@Injectable({ providedIn: "root" })
export class WarehouseApi {
  private readonly http = inject(HttpClient);
  private readonly baseUrl = environment.adminServiceUrl;

  async searchWarehouses(params: WarehouseSearchRequest): Promise<WarehouseSearchResponse> {
    const url = new URL("warehouses", `${this.baseUrl}/`);
    const searchParams = new URLSearchParams();

    if (params.search) {
      searchParams.set("search", params.search);
    }

    if (params.name) {
      searchParams.set("name", params.name);
    }

    if (params.note) {
      searchParams.set("note", params.note);
    }

    if (params.stateProvinceRegion) {
      searchParams.set("stateProvinceRegion", params.stateProvinceRegion);
    }

    if (params.countyDistrict) {
      searchParams.set("countyDistrict", params.countyDistrict);
    }

    if (params.subdistrictMunicipality) {
      searchParams.set("subdistrictMunicipality", params.subdistrictMunicipality);
    }

    if (params.communeVillage) {
      searchParams.set("communeVillage", params.communeVillage);
    }

    if (params.line) {
      searchParams.set("line", params.line);
    }

    if (params.zip) {
      searchParams.set("zip", params.zip);
    }

    if (params.page !== undefined) {
      searchParams.set("page", params.page.toString());
    }

    if (params.limit !== undefined) {
      searchParams.set("limit", params.limit.toString());
    }

    if (params.createdAtFrom) {
      searchParams.set("createdAtFrom", params.createdAtFrom);
    }

    if (params.createdAtTo) {
      searchParams.set("createdAtTo", params.createdAtTo);
    }

    if (searchParams.toString()) {
      url.search = searchParams.toString();
    }

    return await firstValueFrom(this.http.get<WarehouseSearchResponse>(url.toString(), { withCredentials: true }));
  }

  async deleteWarehouse(warehouseId: string): Promise<void> {
    const url = new URL(`warehouses/${warehouseId}`, `${this.baseUrl}/`);
    await firstValueFrom(this.http.delete<void>(url.toString(), { withCredentials: true }));
  }

  async createWarehouse(payload: CreateWarehouseRequest): Promise<Warehouse> {
    const url = new URL("warehouses", `${this.baseUrl}/`);
    return await firstValueFrom(this.http.post<Warehouse>(url.toString(), payload, { withCredentials: true }));
  }

  async updateWarehouse(id: string, payload: Partial<CreateWarehouseRequest>): Promise<Warehouse | null> {
    const url = new URL(`warehouses/${id}`, `${this.baseUrl}/`);
    return await firstValueFrom(this.http.put<Warehouse>(url.toString(), payload, { withCredentials: true }));
  }

  async getWarehouseById(id: string): Promise<Warehouse | null> {
    const url = new URL(`warehouses/${id}`, `${this.baseUrl}/`);
    return await firstValueFrom(this.http.get<Warehouse>(url.toString(), { withCredentials: true }));
  }
}
